import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import { formatCurrency, formatDate } from '../utils';
import { IconPlus, IconEye, IconEyeOff } from './Icons';
import { useToast } from './Toast';

const TODAY = new Date().toISOString().slice(0, 10);

// Mały przycisk oka — quick-toggle widoczności w panelu klienta.
// Stoi po prawej obok kwoty; klik nie propaguje do wiersza (edycji).
function VisibilityToggle({ visible, busy, onToggle }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); if (!busy) onToggle(); }}
      title={visible ? 'Widoczna w panelu klienta — kliknij, aby ukryć' : 'Ukryta w panelu klienta — kliknij, aby pokazać'}
      disabled={busy}
      style={{
        background: 'transparent', border: 'none', cursor: busy ? 'wait' : 'pointer',
        padding: '4px', borderRadius: '5px', display: 'inline-flex', alignItems: 'center',
        opacity: busy ? 0.5 : 1, flexShrink: 0,
      }}
    >
      {visible
        ? <IconEye className="w-3.5 h-3.5" style={{ color: 'var(--ok2)' }} />
        : <IconEyeOff className="w-3.5 h-3.5" style={{ color: 'var(--ink3)' }} />}
    </button>
  );
}

function FakturaRow({ f, onClick, onVisibilityChanged }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  // Domyślnie 1 — gdy backend nie odda kolumny (legacy), traktujemy jak widoczną.
  const visible = f.widoczna_w_panelu == null ? true : !!f.widoczna_w_panelu;
  const zaplacona = f.status === 'zaplacona';
  const suma = Number(f.suma) || (f.pozycje || []).reduce((s, p) => s + (Number(p.kwota) || 0), 0);
  const przeterminowana = !zaplacona && f.termin_platnosci && f.termin_platnosci < TODAY;
  const meta = zaplacona
    ? t('clientDetail.paidOn', { date: formatDate(f.data_wplaty || f.data_wystawienia) })
    : (f.termin_platnosci ? t('clientDetail.dueOn', { date: formatDate(f.termin_platnosci) }) : null);

  const toggleVisibility = async () => {
    setBusy(true);
    try {
      const next = !visible;
      await api.setFakturaWidoczna(f.id, next);
      toast.success(next ? 'Faktura widoczna w panelu klienta' : 'Faktura ukryta w panelu klienta');
      onVisibilityChanged && onVisibilityChanged();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div
      onClick={onClick}
      style={{
        background: przeterminowana ? 'rgba(176,88,72,0.06)' : 'var(--bg-card)',
        border: '1px solid ' + (przeterminowana ? 'rgba(176,88,72,0.3)' : 'var(--border)'),
        borderRadius: '7px',
        padding: '7px 10px',
        cursor: 'pointer',
        opacity: visible ? 1 : 0.55,
      }}>
      <div className="flex items-center justify-between gap-2">
        <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--ink)' }} className="truncate">
          {formatDate(f.data_wystawienia)}
          {f.nr_faktury && (
            <span style={{ fontSize: '10px', color: 'var(--ink3)', fontWeight: 400, marginLeft: '8px' }}>{f.nr_faktury}</span>
          )}
        </span>
        <div className="flex items-center gap-2">
          <span style={{ fontFamily: 'monospace', fontSize: '11px', fontWeight: 700, color: zaplacona ? 'var(--ok2)' : 'var(--acc2)' }}>
            {formatCurrency(suma)}
          </span>
          <VisibilityToggle visible={visible} busy={busy} onToggle={toggleVisibility} />
        </div>
      </div>
      {(meta || przeterminowana || !visible) && (
        <div className="flex items-center justify-between gap-2 mt-0.5">
          <span style={{ fontSize: '10px', color: 'var(--ink3)' }}>
            {meta || ''}
            {!visible && <span style={{ marginLeft: meta ? '8px' : 0, color: 'var(--ink3)', fontStyle: 'italic' }}>ukryta w panelu klienta</span>}
          </span>
          {przeterminowana && (
            <span style={{ fontSize: '9px', fontWeight: 700, color: 'var(--err2)' }}>{t('spaceDetail.overdue')}</span>
          )}
        </div>
      )}
    </div>
  );
}

function PakietRow({ p, onClick, onVisibilityChanged }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const visible = p.widoczna_w_panelu == null ? true : !!p.widoczna_w_panelu;
  const zaplacony = p.status === 'zaplacone';
  const meta = zaplacony ? t('clientDetail.packagePaidOn', { date: formatDate(p.data_platnosci || p.data_sprzedazy) }) : null;

  const toggleVisibility = async () => {
    setBusy(true);
    try {
      const next = !visible;
      await api.setPakietWidoczna(p.id, next);
      toast.success(next ? 'Pakiet widoczny w panelu klienta' : 'Pakiet ukryty w panelu klienta');
      onVisibilityChanged && onVisibilityChanged();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: '7px', padding: '7px 10px',
        cursor: 'pointer',
        opacity: visible ? 1 : 0.55,
      }}>
      <div className="flex items-center justify-between gap-2">
        <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--ink)' }} className="truncate">
          {formatDate(p.data_sprzedazy)}
          {p.nr_faktury && (
            <span style={{ fontSize: '10px', color: 'var(--ink3)', fontWeight: 400, marginLeft: '8px' }}>{p.nr_faktury}</span>
          )}
          <span style={{ fontSize: '10px', color: 'var(--ink2)', fontWeight: 400, marginLeft: '8px' }}>· {p.nazwa}</span>
        </span>
        <div className="flex items-center gap-2">
          <span style={{ fontFamily: 'monospace', fontSize: '11px', fontWeight: 700, color: zaplacony ? 'var(--ok2)' : 'var(--acc2)' }}>
            {formatCurrency(p.kwota)}
          </span>
          <VisibilityToggle visible={visible} busy={busy} onToggle={toggleVisibility} />
        </div>
      </div>
      {(meta || !visible) && (
        <div className="flex items-center justify-between gap-2 mt-0.5">
          <span style={{ fontSize: '10px', color: 'var(--ink3)' }}>
            {meta || ''}
            {!visible && <span style={{ marginLeft: meta ? '8px' : 0, color: 'var(--ink3)', fontStyle: 'italic' }}>ukryty w panelu klienta</span>}
          </span>
          <span style={{ fontSize: '10px', color: 'var(--ink3)' }}>{p.liczba_spacerow} {t('clientDetail.spacesAbbrev')}</span>
        </div>
      )}
    </div>
  );
}

function Section({ title, items, total, onAdd, addLabel, totalColor, render }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span style={{ fontSize: '9px', fontWeight: 700, color: 'var(--ink3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {title} {items.length > 0 && <span style={{ color: 'var(--ink3)' }}>({items.length})</span>}
        </span>
        <div className="flex items-center gap-3">
          {items.length > 0 && (
            <span style={{ fontSize: '10px', fontFamily: 'monospace', fontWeight: 700, color: totalColor }}>
              {formatCurrency(total)}
            </span>
          )}
          {onAdd && (
            <button
              onClick={onAdd}
              className="inline-flex items-center gap-1 rounded-btn"
              style={{ background: 'var(--acc)', color: '#fff', border: '1px solid rgba(184,84,40,0.6)', fontSize: '9px', fontWeight: 700, letterSpacing: '0.04em', padding: '4px 10px' }}>
              <IconPlus className="w-3 h-3" /> {addLabel}
            </button>
          )}
        </div>
      </div>
      {items.length === 0 ? (
        <p style={{ fontSize: '10px', color: 'var(--ink3)' }} className="italic mb-1">—</p>
      ) : (
        <div className="flex flex-col gap-1.5">{items.map(render)}</div>
      )}
    </div>
  );
}

/**
 * Wspólna lista faktur + pakietów dla widoku klienta.
 * mode="unpaid" — pokazuje tylko niezapłacone, total w --acc2, brak przycisków Add, zwraca null jeśli pusta
 * mode="all"    — pokazuje wszystkie pozycje, total w --ink2, ma przyciski Add
 */
export default function FakturyList({
  mode = 'all',
  faktury = [],
  pakiety = [],
  onEditFaktura,
  onEditPakiet,
  onAddFaktura,
  onAddPakiet,
  onVisibilityChanged,
}) {
  const { t } = useTranslation();
  const isUnpaid = mode === 'unpaid';

  const fakturyShown = isUnpaid ? faktury.filter(f => f.status !== 'zaplacona') : faktury;
  const pakietyShown = isUnpaid ? pakiety.filter(p => p.status !== 'zaplacone') : pakiety;

  if (isUnpaid && fakturyShown.length === 0 && pakietyShown.length === 0) return null;

  const sumaFaktur = fakturyShown.reduce(
    (s, f) => s + (Number(f.suma) || (f.pozycje || []).reduce((ss, p) => ss + (Number(p.kwota) || 0), 0)),
    0
  );
  const sumaPakietow = pakietyShown.reduce((s, p) => s + Number(p.kwota || 0), 0);
  const totalColor = isUnpaid ? 'var(--acc2)' : 'var(--ink2)';

  return (
    <div className="ds-card overflow-hidden mb-4">
      <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid var(--border)' }}>
        <h2 style={{ fontSize: '11px', fontWeight: 700, color: 'var(--ink)' }}>
          {isUnpaid ? t('clientDetail.unpaidTitle') : t('clientDetail.historyTitle')}
        </h2>
        <span style={{ fontSize: '10px', color: 'var(--ink3)' }}>
          {t('common.total')}: <span style={{ color: totalColor, fontFamily: 'monospace', fontWeight: 700 }}>
            {formatCurrency(sumaFaktur + sumaPakietow)}
          </span>
        </span>
      </div>
      <div className="flex flex-col gap-4 p-4">
        <Section
          title={t('clientDetail.sectionHosting')}
          items={fakturyShown}
          total={sumaFaktur}
          totalColor={totalColor}
          onAdd={isUnpaid ? null : onAddFaktura}
          addLabel={t('clientDetail.addInvoice')}
          render={f => <FakturaRow key={`f${f.id}`} f={f} onClick={() => onEditFaktura && onEditFaktura(f)} onVisibilityChanged={onVisibilityChanged} />}
        />
        <Section
          title={t('clientDetail.sectionPackages')}
          items={pakietyShown}
          total={sumaPakietow}
          totalColor={totalColor}
          onAdd={isUnpaid ? null : onAddPakiet}
          addLabel={t('clientDetail.addPackage')}
          render={p => <PakietRow key={`p${p.id}`} p={p} onClick={() => onEditPakiet && onEditPakiet(p)} onVisibilityChanged={onVisibilityChanged} />}
        />
      </div>
    </div>
  );
}
